'use client';

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { EditModal } from '@/components/ui/edit-modal';
import { toast } from '@/components/ui/use-toast';
import { updateTeamAvailabilityAsManager } from '@/api/registrations/registrationsApi';
import { handleGlobalError } from '@/utils/error-handler';
import type { Availability } from '@/types/tournament.model';
import {
  AvailabilityPicker,
  buildAvailabilityFromSelection,
  getMissingAvailabilityMessage,
  toSelection,
} from './AvailabilityPicker';
import type { FinalDayRule } from './final-day-rule';
import type { KnockoutStart } from '@/utils/tournament-schedule';

interface EditAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournamentId: string;
  registrationId: string;
  /** Shown in the title so the manager knows whose hours they are editing. */
  teamLabel: string;
  /**
   * What the team declared so far; pre-ticked in the picker. Null while the latest copy is being
   * fetched: the save replaces the whole set, so ticking from a stale copy would silently revert
   * whatever the pair changed in the app since the page loaded.
   */
  availability: Availability[] | null;
  tournamentSchedule: Availability[];
  /** What the last day is for: decides what the pair has to declare before it, and the picker's copy. */
  finalDayRule?: FinalDayRule;
  knockoutStart?: KnockoutStart | null;
  /** Refetches the tournament; awaited before closing. */
  onSaved: () => void | Promise<void>;
}

export function EditAvailabilityModal({
  isOpen,
  onClose,
  tournamentId,
  registrationId,
  teamLabel,
  availability,
  tournamentSchedule,
  finalDayRule,
  knockoutStart,
  onSaved,
}: EditAvailabilityModalProps) {
  const [selectedByDate, setSelectedByDate] = useState<Record<
    string,
    Set<string>
  > | null>(() => (availability ? toSelection(availability) : null));
  if (selectedByDate === null && availability) {
    setSelectedByDate(toSelection(availability));
  }
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedByDate) return;
    const next = buildAvailabilityFromSelection(
      tournamentSchedule,
      selectedByDate,
      knockoutStart,
    );
    const missing = getMissingAvailabilityMessage(
      tournamentSchedule,
      next,
      finalDayRule,
      knockoutStart,
    );
    if (missing) {
      toast({
        title: 'Disponibilidad vacía',
        description: missing,
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      await updateTeamAvailabilityAsManager(tournamentId, registrationId, next);
      toast({
        title: 'Disponibilidad actualizada',
        description: `Se guardaron los horarios de ${teamLabel}.`,
      });
      // Awaited so the card already holds the new hours when the modal closes.
      await onSaved();
      onClose();
    } catch (err) {
      handleGlobalError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Closing mid-save would drop the ticks, with nothing left to retry from if the PUT fails.
  const handleClose = () => {
    if (!submitting) onClose();
  };

  return (
    <EditModal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Editar disponibilidad — ${teamLabel}`}
      onSave={handleSubmit}
      isLoading={submitting}
      disabled={!selectedByDate}
      size="default"
      mobileFullScreen
    >
      <div className="max-h-[70vh] overflow-y-auto py-4 max-sm:max-h-none">
        {selectedByDate ? (
          <AvailabilityPicker
            schedule={tournamentSchedule}
            selectedByDate={selectedByDate}
            onChange={setSelectedByDate}
            finalDayRule={finalDayRule}
            knockoutStart={knockoutStart}
            description="Quedan marcados los turnos que la pareja declaró. Ajustalos según lo que te pidió; se pisa la disponibilidad completa."
          />
        ) : (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    </EditModal>
  );
}
